import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { getErrorString, throwUnsupportedValue } from 'src/utils'
import {
  reloadOneOrganization,
  reloadOrganizations,
} from '../../Onboarding/api'
import {
  getOrganizations,
  getOrganization,
  getOrganizationNavigate,
} from './api'
import {
  listConnectUsers,
  listCognitoUsers,
  createCognitoUser,
  deleteCognitoUser,
} from './Users/api'
import { toast } from 'sonner'
import { useAuth } from 'src/auth'

// prettier-ignore
export const keys = {
  all: ['ALL'] as const,
  getOrganizations: () => [...keys.all, 'GET_ORGANIZATIONS'] as const,
  getOrg: (orgId: string) => [...keys.all, 'GET_ORG', orgId] as const,
  usersAll: (orgId: string) => [...keys.all, 'USERS', orgId] as const,
  usersConnect: (orgId: string) => [...keys.usersAll(orgId), 'CONNECT'] as const,
  usersCognito: (orgId: string) => [...keys.usersAll(orgId), 'COGNITO'] as const,
}

export function useGetOrganizations(
  params: Parameters<typeof getOrganizations>[0],
) {
  return useQuery(keys.getOrganizations(), () => getOrganizations(params), {
    onError: (error) => {
      toast.error(getErrorString(error))
    },
  })
}

export function useGetOrganization(
  params: Parameters<typeof getOrganization>[0],
  options?: {
    enabled?: boolean
    staleTime?: number
    refetchOnWindowFocus?: boolean
  },
) {
  return useQuery(
    keys.getOrg(params.organization),
    () => getOrganization(params),
    {
      ...options,
      onError: (error) => {
        toast.error(getErrorString(error))
      },
    },
  )
}

export function useGetOrganizationNavigateSetup(
  params: Parameters<typeof getOrganization>[0],
) {
  const navigate = useNavigate()
  const auth = useAuth()

  return useQuery(
    keys.getOrg(params.organization),
    () => getOrganizationNavigate(params),
    {
      onSuccess: (data) => {
        switch (data.setupStep) {
          case 'setup':
            navigate('/onboarding/login')
            break
          case 'salesforce-configuration':
            navigate('/setup-salesforce/config')
            break
          case 'finished':
            navigate(`/org/${data.organization}/info`)
            break
          default:
            throwUnsupportedValue(data.setupStep)
        }
      },
      onError: (error) => {
        toast.error(getErrorString(error))
        auth.logout()
      },
    },
  )
}

export function useListConnectUsers(
  params: Parameters<typeof listConnectUsers>[0],
) {
  return useQuery(
    keys.usersConnect(params.organization),
    () => listConnectUsers(params),
    {
      onError: (error) => toast.error(getErrorString(error)),
    },
  )
}

export function useListCognitoUsers(
  params: Parameters<typeof listCognitoUsers>[0],
) {
  return useQuery(
    keys.usersCognito(params.organization),
    () => listCognitoUsers(params),
    {
      onError: (error) => {
        toast.error(getErrorString(error))
      },
    },
  )
}

export function useCreateCognitoUser() {
  const queryClient = useQueryClient()

  return useMutation(createCognitoUser, {
    onSuccess: (data, params, context) => {
      queryClient.invalidateQueries(keys.usersAll(params.organization))
    },
    onError: (error, params, context) => {
      toast.error(getErrorString(error))
    },
  })
}

export function useDeleteCognitoUser() {
  const queryClient = useQueryClient()

  return useMutation(deleteCognitoUser, {
    onSuccess: (data, params, context) => {
      queryClient.invalidateQueries(keys.usersAll(params.organization))
    },
    onError: (error, params, context) => {
      toast.error(getErrorString(error))
    },
  })
}

export function useGetOrganizationMutation() {
  return useMutation(getOrganization, {
    onError: (error) => {
      toast.error(getErrorString(error))
    },
  })
}

export function useReloadOrganization() {
  return useMutation(reloadOneOrganization)
}

export function useReloadOrganizations() {
  return useMutation(reloadOrganizations)
}
